/**
 * Application entry point
 *
 * Loads environment configuration, validates it, and renders the application
 * with the appropriate authentication provider.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { AuthProvider } from './auth';
import { getConfig } from './config';
import { ConfigValidationError, ConfigError } from './config/errors';
import { ConfigErrorPage } from './components/ConfigErrorPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthGate } from './components/AuthGate';
import { DevModeProvider } from './hooks/useDevModeConfig.js';
import { ThemeProvider } from '@fti/design-system';
import '@fti/design-system/styles.css';

/**
 * Initialize and render application
 */
try {
  // Load and validate configuration
  const config = getConfig();

  // Log environment for debugging
  console.log(`Starting application in ${config.displayName} environment`);
  if (config.isMock) {
    console.log('Using mock authentication (no external Cognito calls)');
  } else {
    console.log(`Cognito authority: ${config.cognitoAuthority}`);
  }

  // Render application with validated configuration
  // DevModeProvider wraps everything to provide runtime config switching
  // AuthGate wraps App to enforce authentication:
  // - Shows loading indicator while checking auth status
  // - Auto-redirects unauthenticated users to login
  // - Renders App when authenticated
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
      <ThemeProvider defaultTheme="light">
        <ErrorBoundary>
          <DevModeProvider>
            <AuthProvider config={config}>
              <AuthGate>
                <App />
              </AuthGate>
            </AuthProvider>
          </DevModeProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>
  );
} catch (error) {
  // Handle configuration errors gracefully
  if (error instanceof ConfigValidationError || error instanceof ConfigError) {
    console.error('Configuration validation failed:', error);

    // Render configuration error page
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <React.StrictMode>
        <ThemeProvider defaultTheme="light">
          <ConfigErrorPage error={error} />
        </ThemeProvider>
      </React.StrictMode>
    );
  } else {
    // Re-throw unexpected errors
    console.error('Unexpected error during application initialization:', error);
    throw error;
  }
}
